import { Button } from "@platform/ui";
import {
  codeValue,
  ErrorText,
  errorKey,
  Field,
  StepPanel,
  TotpQr,
  useSession,
  useText,
} from "@platform/ui/features";
import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

type Step =
  | { readonly kind: "email" }
  | { readonly kind: "code" }
  | { readonly kind: "totp"; readonly challengeId: string }
  | {
      readonly kind: "enrol";
      readonly challengeId: string;
      readonly otpauthUri: string;
      readonly secret: string;
    };

/** Operator sign-in (ADM-1): an email code, then TOTP, enrolled on the very first sign-in (ADR 0015). */
export function SignInPage() {
  const { t } = useText();
  const { session, state } = useSession();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from ?? "/";
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  if (state.status === "signed_in") return <Navigate to={from} replace />;

  const run = (work: () => Promise<void>) => async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  const start = run(async () => {
    const { error: problem } = await session.api.POST("/v1/ops/auth/email/start", {
      body: { email: email.trim() },
    });
    if (problem !== undefined) return setError(t(errorKey(problem)));
    setCode("");
    setStep({ kind: "code" });
  });

  const verify = run(async () => {
    const { data, error: problem } = await session.api.POST("/v1/ops/auth/email/verify", {
      body: { email: email.trim(), code },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setCode("");
    setStep(
      data.status === "mfa_enrolment_required"
        ? { kind: "enrol", challengeId: data.challengeId, otpauthUri: data.otpauthUri, secret: data.secret }
        : { kind: "totp", challengeId: data.challengeId },
    );
  });

  const finish = run(async () => {
    if (step.kind !== "totp" && step.kind !== "enrol") return;
    const { data, error: problem } = await session.api.POST("/v1/ops/auth/mfa/totp/verify", {
      body: { challengeId: step.challengeId, code },
    });
    if (data === undefined) {
      setCode("");
      return setError(t(errorKey(problem)));
    }
    await session.accept(data.tokens);
    navigate(from, { replace: true });
  });

  const codeField = (id: string) => (
    <Field
      id={id}
      label={t("auth.codeLabel")}
      inputMode="numeric"
      autoComplete="one-time-code"
      value={code}
      onChange={(event) => setCode(codeValue(event.target.value))}
      required
    />
  );

  if (step.kind === "enrol" || step.kind === "totp") {
    const enrol = step.kind === "enrol";
    return (
      <StepPanel
        title={t(enrol ? "admin.enrolTitle" : "auth.mfaTitle")}
        subtitle={t(enrol ? "admin.enrolSubtitle" : "auth.mfaPrompt")}
      >
        {enrol && (
          <>
            <TotpQr uri={step.otpauthUri} />
            <p className="break-all text-center text-caption text-ink-muted" data-testid="totp-secret">
              {t("onboarding.totpManual", { secret: step.secret })}
            </p>
          </>
        )}
        <form className="flex flex-col gap-4" onSubmit={(event) => void finish(event)}>
          {codeField("totp-code")}
          {error !== undefined && <ErrorText>{error}</ErrorText>}
          <Button type="submit" disabled={busy || code.length !== 6}>
            {t("auth.verify")}
          </Button>
        </form>
      </StepPanel>
    );
  }

  return (
    <StepPanel
      title={t("admin.signInTitle")}
      {...(step.kind === "code" ? { subtitle: t("auth.codeSentTo", { target: email }) } : {})}
    >
      {step.kind === "email" ? (
        <form className="flex flex-col gap-4" onSubmit={(event) => void start(event)}>
          <Field
            id="email"
            type="email"
            autoComplete="username"
            label={t("admin.emailLabel")}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          {error !== undefined && <ErrorText>{error}</ErrorText>}
          <Button type="submit" disabled={busy || email.trim() === ""}>
            {t("auth.sendCode")}
          </Button>
        </form>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(event) => void verify(event)}>
          {codeField("email-code")}
          {error !== undefined && <ErrorText>{error}</ErrorText>}
          <Button type="submit" disabled={busy || code.length !== 6}>
            {t("auth.verify")}
          </Button>
        </form>
      )}
    </StepPanel>
  );
}
